// =============================================================================
// src/hooks/useMyAgents.js
//
// Returns the current user's paired agents, polled every 30s.
// Much simpler than per-source polling: one hook, one endpoint, one poll.
//
// Used by <AgentSyncBar /> to show agent status + the Sync button.
// =============================================================================

import { useEffect, useState, useCallback } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const POLL_MS = 30_000;

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem('auditpros_user');
    if (!saved) return {};
    const parsed = JSON.parse(saved);
    const account = parsed?.account;
    const email = account?.idTokenClaims?.email
      || account?.idTokenClaims?.preferred_username
      || account?.idTokenClaims?.emails?.[0]
      || account?.username || account?.email;
    const h = { 'Content-Type': 'application/json' };
    if (email) h['x-user-email'] = email;
    return h;
  } catch { return {}; }
}

/**
 * @returns {{
 *   agents: Array,                // all paired agents (online + offline)
 *   onlineAgents: Array,          // subset that are online right now
 *   primaryAgent: Object|null,    // best agent to use for actions (first online)
 *   loading: boolean,
 *   error: string|null,
 *   refresh: Function,
 * }}
 */
export function useMyAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/agents/mine`, { headers: getAuthHeaders() });
      if (!res.ok) {
        const msg = res.status === 404
          ? 'Agent management not available'
          : `HTTP ${res.status}`;
        setError(msg);
        setLoading(false);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.agents || []);
      setAgents(list);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOnce();
    const timer = setInterval(() => {
      if (!document.hidden) fetchOnce();
    }, POLL_MS);

    const onVis = () => { if (!document.hidden) fetchOnce(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchOnce]);

  // Derive online list and primary
  const onlineAgents = agents.filter(a => a.isOnline || a.is_online);
  const primaryAgent = onlineAgents[0] || null;

  return {
    agents,
    onlineAgents,
    primaryAgent,
    loading,
    error,
    refresh: fetchOnce,
  };
}
